import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, INK_BLACK } from "../../../theme/tokens";
import { AffectedNurseRow } from "./AffectedNurseRow";
import { AlertDetailVisual } from "./AlertDetailVisual";
import type { DepartmentAlert } from "./types";
import {
	AFFECTED_NURSES_DISPLAY_LIMIT,
	ALERT_TYPE_COLORS,
	ALERT_TYPE_I18N_KEYS,
	ALERT_TYPE_ICONS,
	DETAIL_BG,
	getAlertSummaryText,
	keyboardClickProps,
	SCHEDULED_BADGE_COLORS,
	SEVERITY_COLORS,
} from "./utils";

type Props = {
	alert: DepartmentAlert;
};

export const AlertCard: FC<Props> = memo(({ alert }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const toggle = useCallback(() => setExpanded((p) => !p), []);

	const sevColors = SEVERITY_COLORS[alert.severity];
	const typeColors = ALERT_TYPE_COLORS[alert.alertType];
	const Icon = ALERT_TYPE_ICONS[alert.alertType];
	const typeLabel = t(ALERT_TYPE_I18N_KEYS[alert.alertType]);

	const affectedNurses = alert.detail?.affectedNurses ?? [];

	return (
		<Box
			sx={{
				bgcolor: "white",
				borderRadius: "8px",
				border: "1px solid",
				borderColor: "grey.200",
				overflow: "hidden",
				transition: "all 0.2s ease",
				boxShadow: expanded ? `0px 2px 8px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}` : "none",
				"&:hover": { borderColor: "grey.300", boxShadow: `0px 1px 4px ${alpha(INK_BLACK, 0.06)}` },
				position: "relative",
			}}
		>
			{/* Severity left accent bar */}
			<Box
				sx={{
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: 3,
					bgcolor: sevColors.accent,
					borderRadius: "8px 0 0 8px",
				}}
			/>
			{/* Main row */}
			<Box
				onClick={toggle}
				{...keyboardClickProps(toggle)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					px: 1,
					pl: 1.5,
					py: 1.5,
					"&:hover": { bgcolor: "grey.50" },
					transition: "background-color 0.15s",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flex: 1,
						minWidth: 0,
					}}
				>
					{/* Icon */}
					<Box
						sx={{
							width: 32,
							height: 32,
							borderRadius: "50%",
							bgcolor: typeColors.iconBg,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<Icon sx={{ fontSize: 18, color: typeColors.text }} />
					</Box>

					{/* Title + summary */}
					<Box
						sx={{
							minWidth: 0,
							flex: 1,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.75,
							}}
						>
							<Typography
								variant="body2"
								noWrap
								sx={{
									fontWeight: 700,
								}}
							>
								{alert.count} {typeLabel}
							</Typography>
							{alert.source === "scheduled" && (
								<Chip
									label={t("alerts.source.scheduled")}
									size="small"
									sx={{ bgcolor: SCHEDULED_BADGE_COLORS.bg, color: SCHEDULED_BADGE_COLORS.text, fontWeight: 600, fontSize: "0.6rem", height: 18 }}
								/>
							)}
						</Box>
						<Typography
							variant="caption"
							noWrap
							sx={{
								color: "text.secondary",
							}}
						>
							{getAlertSummaryText(alert, t)}
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<KeyboardArrowDownIcon
						sx={{
							transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.2s",
							color: "text.secondary",
							fontSize: 20,
						}}
					/>
				</Box>
			</Box>
			{/* Expanded details */}
			<Collapse in={expanded} unmountOnExit>
				<Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid", borderColor: "grey.100", bgcolor: DETAIL_BG }}>
					{/* Details section */}
					<Typography
						variant="caption"
						sx={{
							fontWeight: 700,
							textTransform: "uppercase",
							color: "text.secondary",
							letterSpacing: 0.5,
						}}
					>
						{t("details")}
					</Typography>
					<Box
						sx={{
							mt: 0.5,
							mb: 1.5,
						}}
					>
						<AlertDetailVisual alert={alert} typeColors={typeColors} />
					</Box>

					{/* Affected Staff */}
					{affectedNurses.length > 0 && (
						<>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 700,
									textTransform: "uppercase",
									color: "text.secondary",
									letterSpacing: 0.5,
								}}
							>
								{t("affected.staff")}
							</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 0.5,
									mt: 0.5,
								}}
							>
								{affectedNurses.slice(0, AFFECTED_NURSES_DISPLAY_LIMIT).map((nurse, idx) => (
									<AffectedNurseRow key={`${nurse.nurseId}-${idx}`} nurse={nurse} typeColors={typeColors} />
								))}
								{affectedNurses.length > AFFECTED_NURSES_DISPLAY_LIMIT && (
									<Typography
										variant="caption"
										sx={{
											color: "text.secondary",
										}}
									>
										{t("alerts.more.staff", { count: affectedNurses.length - AFFECTED_NURSES_DISPLAY_LIMIT })}
									</Typography>
								)}
							</Box>
						</>
					)}
				</Box>
			</Collapse>
		</Box>
	);
});
